/**
 * Generic Form Hook
 * 
 * DRY principle - Tüm formlar bu hook'u kullanır.
 * Form state, validation, error handling tek yerde.
 * 
 * @param {Object} initialState - Initial form data
 * @param {Object} validationRules - Validation rules object
 *   Format: { fieldName: (value, formData) => errorString | null }
 * 
 * @returns {Object} Form utilities
 *   - formData: Current form data
 *   - errors: Current validation errors
 *   - handleChange: Input change handler
 *   - validate: Run all validations
 *   - resetForm: Reset to initial state
 *   - setFormData: Manually set form data
 * 
 * @example
 * const { formData, errors, handleChange, validate } = useForm(
 *   { email: '', password: '' },
 *   {
 *     email: validators.email,
 *     password: (val) => validators.required(val, 'Şifre')
 *   }
 * );
 */

import { useState } from 'react';

export const useForm = (initialState, validationRules = {}) => {
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  /**
   * Input change handler
   * Updates formData and clears error for that specific field
   * 
   * @param {Event} e - Input change event
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Update form data
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null
      }));
    }
  };

  /**
   * Input blur handler
   * Marks field as touched and validates it
   * 
   * @param {Event} e - Input blur event
   */
  const handleBlur = (e) => {
    const { name } = e.target;
    
    // Mark field as touched
    setTouched((prev) => ({
      ...prev,
      [name]: true
    }));
    
    // Validate this field if rule exists
    if (validationRules[name]) {
      const error = validationRules[name](formData[name], formData);
      setErrors((prev) => ({
        ...prev,
        [name]: error
      }));
    }
  };

  /**
   * Run all validation rules
   * Sets all errors at once
   * 
   * @returns {Object} Object with all validation errors
   */
  const validate = () => {
    const newErrors = {};
    
    // Run all validation rules
    Object.keys(validationRules).forEach((field) => {
      const validator = validationRules[field];
      const error = validator(formData[field], formData);
      
      if (error) {
        newErrors[field] = error;
      }
    });
    
    setErrors(newErrors);
    
    // Return errors object
    return newErrors;
  };

  /**
   * Reset form to initial state
   * Clears formData, errors, and touched state
   */
  const resetForm = () => {
    setFormData(initialState);
    setErrors({});
    setTouched({});
  };

  return {
    formData,
    errors,
    touched,
    handleChange,
    handleBlur,
    validate,
    resetForm,
    setFormData,
    setErrors,
    setTouched
  };
};

export default useForm;

